import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { LogIn, ArrowRight, LifeBuoy } from 'lucide-react';
import AuthChoiceModal from '../components/AuthChoiceModal';
import Footer from '../components/Footer';
import LanguageSelector from '../components/LanguageSelector';
import WelcomeCarousel from '../components/WelcomeCarousel';

const LOGO_URL = "/logo.jpg";

export default function WelcomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { t } = useT();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authIntent, setAuthIntent] = useState('user');
  const [authMode, setAuthMode] = useState('login');

  // If already authenticated, redirect based on stored intent
  useEffect(() => {
    if (isAuthenticated && !loading) {
      const loginIntent = sessionStorage.getItem('kyloo_login_intent');
      sessionStorage.removeItem('kyloo_login_intent');
      navigate(loginIntent === 'professional' ? '/dashboard' : '/home', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (isAuthenticated && !loading) {
    return null;
  }

  const handleContinueAsGuest = () => {
    sessionStorage.setItem('kyloo_guest_mode', '1');
    navigate('/home');
  };

  const handleAuthSuccess = () => {
    if (authIntent === 'professional') navigate('/dashboard');
    else navigate('/home');
  };

  if (loading || isLoggingIn) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{
          background: 'linear-gradient(135deg, #1a4fd8 0%, #2563EB 50%, #1a4fd8 100%)'
        }}
      >
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-white/80">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{
        background: 'linear-gradient(135deg, #1a4fd8 0%, #2563EB 50%, #1a4fd8 100%)'
      }}
      data-testid="welcome-page"
    >
      {/* Language selector + help button — top right, always visible */}
      <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
        <LanguageSelector variant="header" />
        <button
          type="button"
          onClick={() => navigate('/assistenza')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs sm:text-sm font-medium backdrop-blur-sm border border-white/20 transition-colors"
          data-testid="welcome-need-help"
          aria-label="Richiedi assistenza"
        >
          <LifeBuoy className="w-4 h-4" />
          <span>Ti serve aiuto?</span>
        </button>
      </div>

      {/* Hero: logo + carousel side-by-side on desktop, stacked on mobile */}
      <div className="w-full max-w-6xl flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 py-8">
        {/* Left column: compact branding */}
        <div className="flex-shrink-0 text-center lg:text-left lg:max-w-xs">
          <div className="mb-6 animate-fade-in flex justify-center lg:justify-start">
            <img
              src={LOGO_URL}
              alt="Kyloo"
              className="w-32 h-32 lg:w-36 lg:h-36 rounded-3xl shadow-2xl"
              data-testid="welcome-logo"
            />
          </div>
          <p className="text-white text-2xl lg:text-3xl font-semibold mb-2">
            {t('welcome.tagline')}
          </p>
          <p className="text-white/70 text-sm mb-0">
            {t('welcome.subTagline')}
          </p>
        </div>

        {/* Right column: feature carousel */}
        <div className="w-full lg:flex-1 lg:max-w-2xl">
          <WelcomeCarousel />
        </div>
      </div>

      {/* Buttons container */}
      <div className="w-full max-w-sm space-y-3 pb-8">
        {/* 1. Continue as guest (top) */}
        <Button
          onClick={handleContinueAsGuest}
          variant="ghost"
          className="w-full text-white hover:bg-white/10 py-4 rounded-2xl text-lg font-medium flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/50 transition-all"
          data-testid="guest-button"
        >
          {t('welcome.guest')}
          <ArrowRight className="w-5 h-5" />
        </Button>

        {/* 2. Login (center) */}
        <Button
          onClick={() => { setAuthMode('login'); setAuthIntent('user'); setAuthModalOpen(true); }}
          className="w-full bg-white text-blue-600 hover:bg-white/90 py-4 rounded-2xl text-lg font-semibold flex items-center justify-center gap-3 shadow-lg transition-all hover:scale-[1.02]"
          data-testid="login-button"
        >
          <LogIn className="w-5 h-5" />
          {t('welcome.login')}
        </Button>

        {/* 3. Register (bottom) */}
        <Button
          onClick={() => { setAuthMode('register'); setAuthIntent('user'); setAuthModalOpen(true); }}
          variant="ghost"
          className="w-full text-white hover:bg-white/10 py-4 rounded-2xl text-lg font-semibold flex items-center justify-center gap-3 border-2 border-white bg-white/5 hover:bg-white/15 transition-all"
          data-testid="register-button"
        >
          {t('welcome.register')}
          <ArrowRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Footer */}
      <Footer variant="dark" />

      <AuthChoiceModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        intent={authIntent}
        initialMode={authMode}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}
